import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchPtzState,
  ptzContinuousMove,
  ptzStop,
  type PtzContinuousBody,
  type PtzState,
} from "../api/ptz.ts";

/** Normalized ONVIF velocity (-1…1) per axis. */
export type PtzVelocity = {
  pan?: number;
  tilt?: number;
  zoom?: number;
};

export type PtzDir = "left" | "right" | "up" | "down" | "zoomIn" | "zoomOut";

export type PtzContinuousRunner = (
  body: PtzContinuousBody,
) => Promise<PtzState>;

export type UsePtzContinuousControlOptions = {
  cameraId: string;
  operable?: boolean;
  renewMs?: number;
  zoomHoldTimeoutMs?: number;
  zoomRenewMs?: number;
  onResult?: (state: PtzState) => void;
  onError?: (err: unknown) => void;
  startRunner?: PtzContinuousRunner;
  stopRunner?: (cameraId: string) => Promise<PtzState>;
};

export type UsePtzContinuousControlResult = {
  start: (dir: PtzDir) => void;
  stop: () => Promise<void>;
  moving: PtzDir | null;
  /** @deprecated Use `moving`; kept for control chrome that expected queue counts. */
  counts: Record<PtzDir, number>;
  isFull: (dir: PtzDir) => boolean;
};

const ALL_DIRS: readonly PtzDir[] = [
  "left",
  "right",
  "up",
  "down",
  "zoomIn",
  "zoomOut",
] as const;

const ZERO_COUNTS: Record<PtzDir, number> = {
  left: 0,
  right: 0,
  up: 0,
  down: 0,
  zoomIn: 0,
  zoomOut: 0,
};

const DEFAULT_RENEW_MS = 800;
const DEFAULT_ZOOM_HOLD_TIMEOUT_MS = 45_000;
const DEFAULT_ZOOM_RENEW_MS = 38_000;

function isZoomDir(dir: PtzDir): boolean {
  return dir === "zoomIn" || dir === "zoomOut";
}

function velocityFor(dir: PtzDir): PtzVelocity {
  switch (dir) {
    case "left":
      return { pan: -1 };
    case "right":
      return { pan: 1 };
    case "up":
      return { tilt: -1 };
    case "down":
      return { tilt: 1 };
    case "zoomIn":
      return { zoom: 1 };
    case "zoomOut":
      return { zoom: -1 };
  }
}

function isZoomOnly(vel: PtzVelocity): boolean {
  return (
    vel.zoom !== undefined && vel.pan === undefined && vel.tilt === undefined
  );
}

function bodyForVelocity(
  vel: PtzVelocity,
  zoomHoldTimeoutMs: number,
): PtzContinuousBody {
  const body: PtzContinuousBody = {
    pan: vel.pan,
    tilt: vel.tilt,
    zoom: vel.zoom,
  };
  if (isZoomOnly(vel)) {
    body.timeoutMs = zoomHoldTimeoutMs;
  }
  return body;
}

export function usePtzContinuousControl(
  opts: UsePtzContinuousControlOptions,
): UsePtzContinuousControlResult {
  const {
    cameraId,
    operable = true,
    renewMs = DEFAULT_RENEW_MS,
    zoomHoldTimeoutMs = DEFAULT_ZOOM_HOLD_TIMEOUT_MS,
    zoomRenewMs = DEFAULT_ZOOM_RENEW_MS,
    onResult,
    onError,
    startRunner,
    stopRunner,
  } = opts;

  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const startRunnerRef = useRef(startRunner);
  const stopRunnerRef = useRef(stopRunner);
  const zoomHoldTimeoutRef = useRef(zoomHoldTimeoutMs);
  const zoomRenewMsRef = useRef(zoomRenewMs);
  const panRenewMsRef = useRef(renewMs);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    startRunnerRef.current = startRunner;
  }, [startRunner]);
  useEffect(() => {
    stopRunnerRef.current = stopRunner;
  }, [stopRunner]);
  useEffect(() => {
    zoomHoldTimeoutRef.current = zoomHoldTimeoutMs;
  }, [zoomHoldTimeoutMs]);
  useEffect(() => {
    zoomRenewMsRef.current = zoomRenewMs;
  }, [zoomRenewMs]);
  useEffect(() => {
    panRenewMsRef.current = renewMs;
  }, [renewMs]);

  const [moving, setMoving] = useState<PtzDir | null>(null);
  const movingRef = useRef<PtzDir | null>(null);
  const renewRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRenew = useCallback(() => {
    if (renewRef.current !== null) {
      clearInterval(renewRef.current);
      renewRef.current = null;
    }
  }, []);

  const runStart = useCallback(
    (dir: PtzDir) => {
      const vel = velocityFor(dir);
      const body = bodyForVelocity(vel, zoomHoldTimeoutRef.current);
      const run =
        startRunnerRef.current ??
        ((b: PtzContinuousBody) => ptzContinuousMove(cameraId, b));
      void run(body).catch((err) => onErrorRef.current?.(err));
    },
    [cameraId],
  );

  const stop = useCallback((): Promise<void> => {
    const was = movingRef.current;
    if (!was) return Promise.resolve();
    clearRenew();
    movingRef.current = null;
    setMoving(null);
    const zoomOnly = isZoomDir(was);
    const run =
      stopRunnerRef.current ??
      (async (id: string) => {
        await ptzStop(id, zoomOnly ? { panTilt: false, zoom: true } : {});
        return fetchPtzState(id);
      });
    const p = run(cameraId)
      .then((state) => onResultRef.current?.(state))
      .catch((err) => onErrorRef.current?.(err))
      .then(() => undefined);
    return p;
  }, [cameraId, clearRenew]);

  const scheduleRenew = useCallback(
    (dir: PtzDir) => {
      clearRenew();
      const intervalMs = isZoomDir(dir)
        ? zoomRenewMsRef.current
        : panRenewMsRef.current;
      renewRef.current = setInterval(() => {
        const d = movingRef.current;
        if (d) runStart(d);
      }, intervalMs);
    },
    [clearRenew, runStart],
  );

  const start = useCallback(
    (dir: PtzDir) => {
      if (!operable) return;
      if (movingRef.current === dir) {
        if (isZoomDir(dir)) return;
        runStart(dir);
        return;
      }
      movingRef.current = dir;
      setMoving(dir);
      runStart(dir);
      scheduleRenew(dir);
    },
    [operable, runStart, scheduleRenew],
  );

  useEffect(() => {
    return () => {
      clearRenew();
      if (movingRef.current) {
        movingRef.current = null;
        const run =
          stopRunnerRef.current ??
          (async (id: string) => {
            await ptzStop(id);
          });
        void run(cameraId).catch(() => undefined);
      }
    };
  }, [cameraId, clearRenew]);

  const counts = useMemo(() => {
    if (!moving) return ZERO_COUNTS;
    return { ...ZERO_COUNTS, [moving]: 1 };
  }, [moving]);

  const isFull = useCallback(() => false, []);

  return useMemo(
    () => ({ start, stop, moving, counts, isFull }),
    [start, stop, moving, counts, isFull],
  );
}

export const PTZ_DIRS = ALL_DIRS;
